import React, {
    useEffect,
    useMemo,
    useState,
} from 'react';

import {
    Outlet,
    useLocation,
    useNavigate,
} from 'react-router-dom';

import UpdateNoticeLayout
    from '../components/UpdateNoticeLayout';

import UpdateNoticeSidebar
    from '../components/UpdateNoticeSidebar';

import {
    getUpdateNotices,
} from '../services/updateNoticeService';

import useUpdateNoticeLanguage
    from '../hooks/useUpdateNoticeLanguage';

const UPDATE_NOTICE_PROGRAM_TREE = [
    {
        name: 'Hi-CPS',
        platforms: [
            'Web',
            'Android',
            'iOS',
            'NC Agent',
        ],
    },
    {
        name: 'Hi-Link',
        platforms: [],
    },
    {
        name: 'PG-Solution',
        platforms: [],
    },
];

function normalizeTreeValue(value) {
    return String(value || '')
        .trim()
        .toLowerCase();
}

function buildSidebarTree(notices = []) {
    const noticeItems =
        Array.isArray(notices)
            ? notices
            : [];

    /*
     * 고정된 프로그램/플랫폼 구조를 먼저 생성합니다.
     */
    const sidebarTree =
        UPDATE_NOTICE_PROGRAM_TREE.map(
            (program) => ({
                name: program.name,

                platforms:
                    program.platforms.map(
                        (platformName) => ({
                            name: platformName,
                            notices: [],
                        }),
                    ),

                directNotices: [],
            }),
        );

    /*
     * API에서 받은 공지를 고정된 트리에 연결합니다.
     */
    noticeItems.forEach((notice) => {
        const noticeProgram =
            normalizeTreeValue(
                notice?.program,
            );

        const noticePlatform =
            normalizeTreeValue(
                notice?.platform,
            );

        let programNode =
            sidebarTree.find(
                (program) =>
                    normalizeTreeValue(
                        program.name,
                    ) === noticeProgram,
            );

        /*
         * 고정 목록에 없는 프로그램도
         * 공지가 누락되지 않도록 마지막에 추가합니다.
         */
        if (!programNode) {
            const programName =
                String(
                    notice?.program || '',
                ).trim() || '기타';

            programNode = {
                name: programName,
                platforms: [],
                directNotices: [],
            };

            sidebarTree.push(
                programNode,
            );
        }

        /*
         * PG-Solution처럼 플랫폼 구조가 없는 프로그램은
         * 프로그램 바로 아래에 버전을 표시합니다.
         */
        if (
            programNode.platforms.length === 0 &&
            !noticePlatform
        ) {
            programNode.directNotices.push(
                notice,
            );

            return;
        }

        /*
         * 플랫폼 값이 없고 고정 플랫폼도 없는 경우
         */
        if (
            programNode.platforms.length === 0
        ) {
            programNode.directNotices.push(
                notice,
            );

            return;
        }

        let platformNode =
            programNode.platforms.find(
                (platform) =>
                    normalizeTreeValue(
                        platform.name,
                    ) === noticePlatform,
            );

        /*
         * 고정 목록에 없는 플랫폼도
         * 공지가 누락되지 않도록 추가합니다.
         */
        if (!platformNode) {
            const platformName =
                String(
                    notice?.platform || '',
                ).trim() || 'Common';

            platformNode = {
                name: platformName,
                notices: [],
            };

            programNode.platforms.push(
                platformNode,
            );
        }

        platformNode.notices.push(
            notice,
        );
    });

    return sidebarTree;
}

function getSelectedNoticeSeq(
    pathname,
) {
    const match =
        String(pathname || '').match(
            /\/updatenotices\/(\d+)\/?$/,
        );

    if (!match) {
        return null;
    }

    const seq = Number(match[1]);

    return Number.isFinite(seq)
        ? seq
        : null;
}

export default function UpdateNoticeShellPage() {
    const {
        noticeLanguage,
    } = useUpdateNoticeLanguage();

    const navigate = useNavigate();
    const location = useLocation();

    const [
        notices,
        setNotices,
    ] = useState([]);

    const [
        programFilter,
        setProgramFilter,
    ] = useState(null);

    const [
        platformFilter,
        setPlatformFilter,
    ] = useState(null);

    const [
        expandedPrograms,
        setExpandedPrograms,
    ] = useState({});

    const [
        expandedPlatforms,
        setExpandedPlatforms,
    ] = useState({});

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        error,
        setError,
    ] = useState('');

    useEffect(() => {
        let mounted = true;

        const loadNotices = async () => {
            setLoading(true);
            setError('');

            try {
                const result =
                    await getUpdateNotices(
                        noticeLanguage,
                    );

                if (!mounted) {
                    return;
                }

                setNotices(
                    Array.isArray(result)
                        ? result
                        : [],
                );
            } catch (loadError) {
                if (!mounted) {
                    return;
                }

                setNotices([]);

                setError(
                    loadError?.message
                );
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadNotices();

        return () => {
            mounted = false;
        };
    }, [noticeLanguage]);

    const sidebarTree = useMemo(
        () =>
            buildSidebarTree(
                notices,
            ),
        [notices],
    );

    const selectedNoticeSeq =
        useMemo(
            () =>
                getSelectedNoticeSeq(
                    location.pathname,
                ),
            [location.pathname],
        );

    const selectedNotice =
        useMemo(
            () =>
                notices.find(
                    (notice) =>
                        Number(notice.seq) ===
                        Number(
                            selectedNoticeSeq,
                        ),
                ) || null,
            [
                notices,
                selectedNoticeSeq,
            ],
        );

    /*
     * 최초 공지 조회 시 프로그램과 플랫폼을 펼쳐 둡니다.
     * 사용자가 접은 상태는 다시 강제로 열지 않습니다.
     */
    useEffect(() => {
        if (
            sidebarTree.length === 0
        ) {
            return;
        }

        setExpandedPrograms(
            (previous) => {
                const next = {
                    ...previous,
                };

                sidebarTree.forEach(
                    (program) => {
                        if (
                            !Object.prototype
                                .hasOwnProperty
                                .call(
                                    next,
                                    program.name,
                                )
                        ) {
                            next[
                                program.name
                            ] = true;
                        }
                    },
                );

                return next;
            },
        );

        setExpandedPlatforms(
            (previous) => {
                const next = {
                    ...previous,
                };

                sidebarTree.forEach(
                    (program) => {
                        program.platforms.forEach(
                            (platform) => {
                                const key =
                                    `${program.name}::${platform.name}`;

                                if (
                                    !Object.prototype
                                        .hasOwnProperty
                                        .call(
                                            next,
                                            key,
                                        )
                                ) {
                                    next[key] = true;
                                }
                            },
                        );
                    },
                );

                return next;
            },
        );
    }, [sidebarTree]);

    /*
     * 상세 공지를 열었을 때 해당 프로그램과 플랫폼을 펼칩니다.
     */
    useEffect(() => {
        if (
            !selectedNotice?.program
        ) {
            return;
        }

        const programName =
            String(
                selectedNotice.program,
            );

        setExpandedPrograms(
            (previous) => ({
                ...previous,
                [programName]: true,
            }),
        );

        if (
            selectedNotice.platform
        ) {
            const platformKey =
                `${programName}::${selectedNotice.platform}`;

            setExpandedPlatforms(
                (previous) => ({
                    ...previous,
                    [platformKey]: true,
                }),
            );
        }
    }, [
        selectedNotice?.platform,
        selectedNotice?.program,
        selectedNotice?.seq,
    ]);

    const filteredNotices =
        useMemo(
            () =>
                notices.filter(
                    (notice) => {
                        if (
                            programFilter &&
                            notice.program !==
                            programFilter
                        ) {
                            return false;
                        }

                        if (
                            platformFilter &&
                            notice.platform !==
                            platformFilter
                        ) {
                            return false;
                        }

                        return true;
                    },
                ),
            [
                notices,
                platformFilter,
                programFilter,
            ],
        );

    const handleProgramClick = (
        programName,
    ) => {
        setExpandedPrograms(
            (previous) => ({
                ...previous,

                [programName]:
                    !previous[
                    programName
                    ],
            }),
        );
    };

    const handlePlatformClick = (
        programName,
        platformName,
    ) => {
        const key =
            `${programName}::${platformName}`;

        setExpandedPlatforms(
            (previous) => ({
                ...previous,

                [key]:
                    !previous[key],
            }),
        );
    };

    /*
     * 프로그램명을 누르면 오른쪽 블록에
     * 해당 프로그램 공지 목록을 표시합니다.
     */
    const handleProgramFilter = (
        programName,
    ) => {
        setProgramFilter(
            programName,
        );

        setPlatformFilter(
            null,
        );

        navigate(
            '/updatenotices',
        );
    };

    /*
     * 플랫폼명을 누르면 오른쪽 블록에
     * 해당 플랫폼 공지 목록을 표시합니다.
     */
    const handlePlatformFilter = (
        programName,
        platformName,
    ) => {
        setProgramFilter(
            programName,
        );

        setPlatformFilter(
            platformName,
        );

        navigate(
            '/updatenotices',
        );
    };

    const clearFilter = () => {
        setProgramFilter(null);
        setPlatformFilter(null);

        navigate(
            '/updatenotices',
        );
    };

    return (
        <UpdateNoticeLayout
            sidebar={(
                <UpdateNoticeSidebar
                    sidebarTree={
                        sidebarTree
                    }
                    expandedPrograms={
                        expandedPrograms
                    }
                    expandedPlatforms={
                        expandedPlatforms
                    }
                    selectedNoticeSeq={
                        selectedNoticeSeq
                    }
                    isAllNoticesSelected={
                        !programFilter && !platformFilter
                    }
                    onAllNoticesClick={
                        clearFilter
                    }
                    onProgramClick={
                        handleProgramClick
                    }
                    onPlatformClick={
                        handlePlatformClick
                    }
                    onProgramFilter={
                        handleProgramFilter
                    }
                    onPlatformFilter={
                        handlePlatformFilter
                    }
                />
            )}
        >
            <Outlet
                context={{
                    notices,
                    filteredNotices,
                    sidebarTree,
                    programFilter,
                    platformFilter,
                    loading,
                    error,
                    noticeLanguage,
                    selectedNoticeSeq,
                    clearFilter,
                }}
            />
        </UpdateNoticeLayout>

    );
}
